import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, Navigation, Car, Clock, Phone, User, 
  RefreshCw, AlertCircle, CheckCircle, X
} from 'lucide-react';
import { Button } from '../ui/button';

/**
 * LiveTrackingMap component displays real-time driver location on a map
 * Uses GPS coordinates from the backend to show driver position
 * Falls back to a simple visual tracker if no map API is available
 */
const LiveTrackingMap = ({ 
  ride, 
  driverLocation, 
  pickupCoords, 
  dropoffCoords,
  onRefresh,
  showFullMap = false
}) => {
  const [mapError, setMapError] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (driverLocation) {
      setLastUpdate(new Date());
    }
  }, [driverLocation]);

  // Calculate progress percentage based on ride status
  const getProgressPercentage = () => {
    const statusProgress = {
      'requested': 0,
      'pending_driver': 10,
      'driver_assigned': 20,
      'en_route_pickup': 40,
      'arrived_pickup': 50,
      'in_progress': 70,
      'completed': 100,
      'cancelled': 0
    };
    return statusProgress[ride?.status] || 0;
  };

  // Get status message
  const getStatusMessage = () => {
    const messages = {
      'requested': 'Looking for a driver...',
      'pending_driver': 'Finding available drivers...',
      'driver_assigned': 'Driver assigned! Preparing to depart.',
      'en_route_pickup': 'Driver is on the way to pick you up!',
      'arrived_pickup': 'Driver has arrived at pickup location!',
      'in_progress': 'Ride in progress. Enjoy your trip!',
      'completed': 'Ride completed. Thank you!',
      'cancelled': 'Ride was cancelled.'
    };
    return messages[ride?.status] || 'Loading...';
  };

  // Get status color
  const getStatusColor = () => {
    const colors = {
      'requested': 'text-blue-500',
      'pending_driver': 'text-blue-500',
      'driver_assigned': 'text-purple-500',
      'en_route_pickup': 'text-orange-500',
      'arrived_pickup': 'text-cyan-500',
      'in_progress': 'text-green-500',
      'completed': 'text-green-600',
      'cancelled': 'text-red-500'
    };
    return colors[ride?.status] || 'text-gray-500';
  };

  // Open Google Maps for navigation
  const openNavigation = () => {
    if (ride?.pickup_location?.address && ride?.dropoff_location?.address) {
      const directionsUrl = `https://www.google.com/maps/dir/${encodeURIComponent(ride.pickup_location.address)}/${encodeURIComponent(ride.dropoff_location.address)}`;
      window.open(directionsUrl, '_blank');
    } else if (pickupCoords && dropoffCoords) {
      const directionsUrl = `https://www.google.com/maps/dir/${pickupCoords.lat},${pickupCoords.lng}/${dropoffCoords.lat},${dropoffCoords.lng}`;
      window.open(directionsUrl, '_blank');
    }
  };

  const progress = getProgressPercentage();
  const isActive = ['en_route_pickup', 'arrived_pickup', 'in_progress'].includes(ride?.status);

  return (
    <div className="card overflow-hidden" data-testid="live-tracking-map">
      {/* Map Header */}
      <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-[var(--background-subtle)] to-transparent">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Navigation className="w-5 h-5 text-[var(--primary)]" />
            Live Ride Tracking
          </h3>
          <p className={`text-sm ${getStatusColor()}`}>
            {getStatusMessage()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="text-xs text-[var(--text-muted)]">
              Updated {Math.round((Date.now() - lastUpdate.getTime()) / 1000)}s ago
            </span>
          )}
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Visual Map Representation */}
      <div className={`relative bg-gradient-to-br from-slate-100 to-slate-200 ${showFullMap ? 'h-64' : 'h-48'}`}>
        {/* Progress Route Line */}
        <div className="absolute inset-x-8 top-1/2 transform -translate-y-1/2">
          {/* Background Line */}
          <div className="h-2 bg-slate-300 rounded-full relative">
            {/* Progress Fill */}
            <motion.div 
              className="h-full bg-gradient-to-r from-[#D4AF37] to-green-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
            
            {/* Pickup Marker */}
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2">
              <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-medium whitespace-nowrap">
                Pickup
              </span>
            </div>
            
            {/* Dropoff Marker */}
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                <MapPin className="w-4 h-4 text-white" />
              </div>
              <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs font-medium whitespace-nowrap">
                Dropoff
              </span>
            </div>
            
            {/* Driver Car Marker (animated) */}
            {isActive && (
              <motion.div 
                className="absolute top-1/2 transform -translate-y-1/2"
                initial={{ left: '20%' }}
                animate={{ left: `${Math.min(progress, 90)}%` }}
                transition={{ duration: 0.5 }}
              >
                <div className="relative">
                  <motion.div
                    className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  >
                    <Car className="w-5 h-5 text-white" />
                  </motion.div>
                  {/* Pulse Ring */}
                  <motion.div
                    className="absolute inset-0 bg-[#D4AF37]/30 rounded-full"
                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                  />
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* GPS Coordinates Display */}
        {driverLocation && (
          <div className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            <span className="opacity-70">Driver:</span> {driverLocation.lat?.toFixed(4)}, {driverLocation.lng?.toFixed(4)}
          </div>
        )}

        {/* View in Maps Button */}
        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white"
          onClick={openNavigation}
        >
          <Navigation className="w-4 h-4 mr-1" />
          View in Maps
        </Button>
      </div>

      {/* Route Info */}
      <div className="p-4 space-y-3">
        {/* Pickup */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--text-muted)]">PICKUP</p>
            <p className="font-medium truncate">{ride?.pickup_location?.address || 'Loading...'}</p>
            {ride?.pickup_location?.notes && (
              <p className="text-sm text-blue-600 mt-1">{ride.pickup_location.notes}</p>
            )}
          </div>
        </div>

        {/* Dotted Line */}
        <div className="ml-4 border-l-2 border-dashed border-gray-300 h-3"></div>

        {/* Dropoff */}
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--text-muted)]">DROPOFF</p>
            <p className="font-medium truncate">{ride?.dropoff_location?.address || 'Loading...'}</p>
          </div>
        </div>
      </div>

      {/* Driver Info (when assigned) */}
      {ride?.driver_name && isActive && (
        <div className="p-4 border-t bg-[var(--background-subtle)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4C2C2] flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold">{ride.driver_name}</p>
              <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                <Car className="w-4 h-4" />
                <span className="capitalize">{ride.preferred_vehicle_type || 'Sedan'}</span>
              </div>
            </div>
            {ride.customer_phone && (
              <a 
                href={`tel:${ride.customer_phone}`}
                className="p-3 bg-green-100 rounded-full hover:bg-green-200 transition-colors"
              >
                <Phone className="w-5 h-5 text-green-600" />
              </a>
            )}
          </div>

          {/* ETA */}
          {ride.eta_minutes && (
            <div className="mt-3 p-3 bg-white rounded-lg flex items-center justify-center gap-2">
              <Clock className="w-5 h-5 text-[var(--primary)]" />
              <span className="font-semibold text-lg">{ride.eta_minutes} min</span>
              <span className="text-[var(--text-muted)]">estimated arrival</span>
            </div>
          )}
        </div>
      )}

      {/* Completed State */}
      {ride?.status === 'completed' && (
        <div className="p-4 border-t bg-green-50">
          <div className="flex items-center gap-3 text-green-700">
            <CheckCircle className="w-6 h-6" />
            <div>
              <p className="font-semibold">Ride Completed!</p>
              <p className="text-sm">Thank you for riding with us.</p>
            </div>
          </div>
        </div>
      )}

      {/* Cancelled State */}
      {ride?.status === 'cancelled' && (
        <div className="p-4 border-t bg-red-50">
          <div className="flex items-center gap-3 text-red-700">
            <X className="w-6 h-6" />
            <div>
              <p className="font-semibold">Ride Cancelled</p>
              <p className="text-sm">{ride.cancellation_reason || 'The ride has been cancelled.'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTrackingMap;
